import { clsx, type ClassValue } from 'clsx';

/** Concaténation conditionnelle de classes. */
export function cn(...inputs: ClassValue[]): string {
  return clsx(inputs);
}

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const timeFormatter = new Intl.DateTimeFormat('fr-FR', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const monthFormatter = new Intl.DateTimeFormat('fr-FR', { month: 'short', year: '2-digit' });

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFormatter.format(date);
}

export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : timeFormatter.format(date);
}

/** `2026-07` → `juil. 26`. */
export function formatMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split('-');
  if (!year || !month) return yearMonth;
  return monthFormatter.format(new Date(Number(year), Number(month) - 1, 1));
}

/** Écart lisible : « il y a 3 min », « il y a 2 j ». */
export function formatRelative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);

  if (seconds < 45) return "à l'instant";
  if (seconds < 3600) return `il y a ${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `il y a ${Math.round(seconds / 3600)} h`;
  if (seconds < 604_800) return `il y a ${Math.round(seconds / 86_400)} j`;
  return formatDate(date);
}

const numberFormatter = new Intl.NumberFormat('fr-FR');

export function formatNumber(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : numberFormatter.format(value);
}

export function formatPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined) return '—';
  return `${(ratio * 100).toFixed(digits).replace('.', ',')} %`;
}

export function formatAmount(value: number | null | undefined, currency: string | null): string {
  if (value === null || value === undefined) return '—';
  const formatted = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(value);
  return currency ? `${formatted} ${currency}` : formatted;
}

/** Taille de fichier en unités binaires (Kio, Mio…). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes <= 0) return '—';

  const units = ['o', 'Kio', 'Mio', 'Gio', 'Tio'];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** exponent;

  return `${value.toFixed(exponent === 0 ? 0 : 1).replace('.', ',')} ${units[exponent]}`;
}

/** Initiales d'un nom complet, pour les pastilles d'identité. */
export function initialsOf(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Retarde l'exécution jusqu'à ce que l'appelant se taise.
 * Utilisé par la recherche instantanée : on interroge le serveur quand
 * l'utilisateur a fini de taper, pas à chaque frappe.
 */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  delayMs: number,
): ((...args: A) => void) & { cancel(): void } {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const debounced = (...args: A): void => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delayMs);
  };

  debounced.cancel = (): void => {
    if (timer) clearTimeout(timer);
  };

  return debounced;
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count > 1 ? plural : singular;
}
